import { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import { downloadCsv } from "@/lib/csv";
import { toast } from "sonner";

const ExportCtx = createContext(null);

export function ExportProvider({ children }) {
  const handlerRef = useRef(null);
  const [hasHandler, setHasHandler] = useState(false);

  const setHandler = useCallback((handler) => {
    handlerRef.current = handler;
    setHasHandler(!!handler);
  }, []);

  const trigger = useCallback(() => {
    if (handlerRef.current) handlerRef.current();
    else toast.info("Nothing to export on this page.");
  }, []);

  return (
    <ExportCtx.Provider value={{ setHandler, trigger, hasHandler }}>
      {children}
    </ExportCtx.Provider>
  );
}

export const useExport = () => useContext(ExportCtx);

/** Page hook: register CSV export handler for current page. Auto-unregisters on unmount. */
export function usePageExport({ filename, rows, columns, label }) {
  const { setHandler } = useExport();
  useEffect(() => {
    setHandler(() => {
      if (!rows || rows.length === 0) {
        toast.info("Nothing to export on this page.");
        return;
      }
      downloadCsv(filename, rows, columns);
      toast.success(`${label || "Export"} downloaded (${rows.length} rows)`);
    });
    return () => setHandler(null);
  }, [filename, rows, columns, label, setHandler]);
}
